import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText, ArrowDownLeft, ArrowUpRight, X, MessageSquare, ShieldCheck, ShieldX } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { TradeJournalDetail, TradeJournalRow } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago, krw, num, pct, pnlColor, timeOnly } from '@/lib/format';

type SideFilter = '' | 'BUY' | 'SELL';
type StatusFilter = '' | 'FILLED' | 'PARTIALLY_FILLED' | 'SUBMITTED' | 'CANCELLED' | 'REJECTED' | 'EXPIRED';

const STATUS_TONE: Record<string, string> = {
  FILLED:            'bg-kam-green-soft text-kam-green border-kam-green/25',
  PARTIALLY_FILLED:  'bg-kam-amber-soft text-kam-amber border-kam-amber/30',
  SUBMITTED:         'bg-surface-2 text-text-dim border-[color:var(--border)]',
  APPROVED:          'bg-surface-2 text-text-dim border-[color:var(--border)]',
  CANCELLED:         'bg-surface-2 text-text-faint border-[color:var(--border)]',
  REJECTED:          'bg-kam-red-soft text-kam-red border-kam-red/25',
  EXPIRED:           'bg-surface-2 text-text-faint border-[color:var(--border)]',
};

export function TradeJournal() {
  const [side, setSide] = useState<SideFilter>('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['tradeJournal', side, status],
    queryFn: () => dashboardApi.tradeJournal({
      side: side || undefined,
      status: status || undefined,
    }),
    refetchInterval: 30_000,
  });

  const rows = useMemo<TradeJournalRow[]>(() => data ?? [], [data]);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <ScrollText size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Trade Journal</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/trades'
              : `${rows.length} trades · last 30 days · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip label="All sides" active={side === ''} onClick={() => setSide('')} />
        <FilterChip label="Buy" active={side === 'BUY'} onClick={() => setSide('BUY')} />
        <FilterChip label="Sell" active={side === 'SELL'} onClick={() => setSide('SELL')} />
        <span className="w-px bg-[color:var(--border)] mx-1" />
        <FilterChip label="All status" active={status === ''} onClick={() => setStatus('')} />
        <FilterChip label="Filled" active={status === 'FILLED'} onClick={() => setStatus('FILLED')} />
        <FilterChip label="Partial" active={status === 'PARTIALLY_FILLED'} onClick={() => setStatus('PARTIALLY_FILLED')} />
        <FilterChip label="Rejected" active={status === 'REJECTED'} onClick={() => setStatus('REJECTED')} />
      </div>

      {rows.length === 0 && !isLoading
        ? <div className="card card-tight text-xs text-text-faint italic">No trades match the current filter.</div>
        : <div className="card card-tight overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-text-faint text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-medium py-1.5 px-2">Date</th>
                  <th className="text-left font-medium py-1.5 px-2">Side</th>
                  <th className="text-left font-medium py-1.5 px-2">Code</th>
                  <th className="text-left font-medium py-1.5 px-2">Name</th>
                  <th className="text-right font-medium py-1.5 px-2">Qty</th>
                  <th className="text-right font-medium py-1.5 px-2">Fill px</th>
                  <th className="text-right font-medium py-1.5 px-2">P&amp;L</th>
                  <th className="text-left font-medium py-1.5 px-2">Status</th>
                  <th className="text-left font-medium py-1.5 px-2">By</th>
                  <th className="text-center font-medium py-1.5 px-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row
                    key={r.requestId}
                    r={r}
                    active={selectedId === r.requestId}
                    onClick={() => setSelectedId(r.requestId)}
                  />
                ))}
              </tbody>
            </table>
          </div>}

      {selectedId && <DetailDrawer requestId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-chip text-xs font-medium border transition',
        active
          ? 'bg-kam-violet-soft text-kam-violet border-kam-violet/30'
          : 'bg-surface-2 text-text-dim border-[color:var(--border)] hover:text-text',
      )}
    >
      {label}
    </button>
  );
}

function Row({ r, active, onClick }: { r: TradeJournalRow; active: boolean; onClick: () => void }) {
  const SideIcon = r.side === 'BUY' ? ArrowDownLeft : ArrowUpRight;
  const sideTone = r.side === 'BUY' ? 'text-kam-green' : 'text-kam-red';
  const stat = STATUS_TONE[r.status] ?? 'bg-surface-2 text-text-dim border-[color:var(--border)]';
  return (
    <tr
      onClick={onClick}
      className={cn(
        'border-t border-[color:var(--border)] cursor-pointer transition',
        active ? 'bg-kam-violet/10' : 'hover:bg-surface-2',
      )}
    >
      <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">{r.tradeDate}</td>
      <td className="py-1.5 px-2">
        <span className={cn('inline-flex items-center gap-1', sideTone)}>
          <SideIcon size={12} strokeWidth={2.5} />
          <span className="text-xs font-semibold">{r.side}</span>
        </span>
      </td>
      <td className="py-1.5 px-2 font-mono text-xs">{r.code}</td>
      <td className="py-1.5 px-2 truncate max-w-[180px]" title={r.name}>{r.name}</td>
      <td className="py-1.5 px-2 text-right tabular-nums font-mono text-xs">
        {r.filledQty > 0 ? num(r.filledQty) : <span className="text-text-faint">{num(r.requestedQty)}*</span>}
      </td>
      <td className="py-1.5 px-2 text-right tabular-nums font-mono text-xs">
        {r.avgFillPrice != null ? num(r.avgFillPrice) : '—'}
      </td>
      <td className={cn('py-1.5 px-2 text-right tabular-nums font-mono text-xs font-semibold', pnlColor(r.realizedPnl))}>
        {r.realizedPnl != null
          ? <>
              {krw(r.realizedPnl, { sign: true })}
              {r.realizedPnlPct != null && <span className="text-[10px] opacity-70 ml-1">{pct(r.realizedPnlPct, { sign: true })}</span>}
            </>
          : <span className="text-text-faint">—</span>}
      </td>
      <td className="py-1.5 px-2">
        <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded-chip text-[10px] font-semibold border', stat)}>
          {r.status === 'PARTIALLY_FILLED' ? 'PARTIAL' : r.status}
        </span>
      </td>
      <td className="py-1.5 px-2 text-xs font-mono text-text-dim truncate max-w-[100px]" title={r.requestedBy}>
        {r.requestedBy}
      </td>
      <td className="py-1.5 px-2 text-center">
        <span className="inline-flex items-center gap-1 text-text-faint">
          {r.hasRationale && <MessageSquare size={11} className="text-text-dim" />}
          {r.riskApprovedBy && <ShieldCheck size={11} className="text-kam-green" />}
          {r.hasRiskDecline && <ShieldX size={11} className="text-kam-red" />}
        </span>
      </td>
    </tr>
  );
}

function DetailDrawer({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tradeJournalDetail', requestId],
    queryFn: () => dashboardApi.tradeJournalDetail(requestId),
  });

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-xl bg-bg border-l border-[color:var(--border)] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-bg border-b border-[color:var(--border)] px-4 py-3 flex items-center gap-2">
          <ScrollText size={16} className="text-text-dim" />
          <h2 className="text-sm font-semibold flex-1 truncate font-mono">{requestId}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-2 text-text-dim" title="Close">
            <X size={16} />
          </button>
        </div>

        {isLoading && <div className="p-6 text-xs text-text-faint">Loading…</div>}
        {isError && <div className="p-6 text-xs text-kam-red">Failed to load trade.</div>}
        {data && <Detail d={data} />}
      </aside>
    </div>
  );
}

function Detail({ d }: { d: TradeJournalDetail }) {
  const SideIcon = d.side === 'BUY' ? ArrowDownLeft : ArrowUpRight;
  const sideTone = d.side === 'BUY' ? 'text-kam-green' : 'text-kam-red';
  return (
    <div className="p-4 space-y-5">
      {/* Headline */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className={cn('inline-flex items-center gap-1', sideTone)}>
            <SideIcon size={14} strokeWidth={2.5} />
            <span className="text-sm font-bold">{d.side}</span>
          </span>
          <span className="text-base font-semibold">{d.name}</span>
          <span className="text-xs font-mono text-text-faint">{d.code}</span>
          <span className={cn(
            'ml-auto inline-flex items-center px-2 py-0.5 rounded-chip text-[10px] font-semibold border',
            STATUS_TONE[d.status] ?? 'bg-surface-2 text-text-dim border-[color:var(--border)]',
          )}>{d.status === 'PARTIALLY_FILLED' ? 'PARTIAL' : d.status}</span>
        </div>
        <div className="text-xs text-text-faint">
          {d.tradeDate} · {d.market} · {d.sector ?? '—'}
        </div>
      </div>

      {/* Realized P&L (SELLs only) */}
      {d.realizedPnl != null && (
        <div className="card card-tight border border-kam-violet/30 bg-kam-grad-soft">
          <div className="text-[10px] uppercase tracking-wide text-text-faint mb-1">Realized P&amp;L</div>
          <div className="flex items-baseline gap-2">
            <span className={cn('text-2xl font-bold font-mono tabular-nums', pnlColor(d.realizedPnl))}>
              {krw(d.realizedPnl, { sign: true })}
            </span>
            {d.realizedPnlPct != null && (
              <span className={cn('text-sm font-mono tabular-nums', pnlColor(d.realizedPnlPct))}>
                {pct(d.realizedPnlPct, { sign: true })}
              </span>
            )}
          </div>
          <div className="text-xs text-text-dim mt-1">
            Avg cost {d.avgCostAtSell != null ? num(d.avgCostAtSell) : '—'}
            {' '}· basis {d.costBasis != null ? krw(d.costBasis) : '—'}
          </div>
        </div>
      )}

      {/* Order specifics */}
      <Section title="Order">
        <KV label="Type">{d.orderType ?? '—'}</KV>
        <KV label="Requested qty">{num(d.requestedQty)}</KV>
        <KV label="Filled qty">{num(d.filledQty)}</KV>
        <KV label="Limit price">{d.limitPrice != null ? num(d.limitPrice) : '—'}</KV>
        <KV label="Avg fill">{d.avgFillPrice != null ? num(d.avgFillPrice) : '—'}</KV>
        <KV label="Max slippage">{d.maxSlippagePct != null ? pct(d.maxSlippagePct, { sign: false }) : '—'}</KV>
        <KV label="Urgency">{d.urgency ?? '—'}</KV>
        <KV label="TIF">{d.timeInForce ?? '—'}</KV>
        <KV label="Target time">{d.targetTime ?? '—'}</KV>
      </Section>

      {/* Price strategy (only when set) */}
      {d.priceStrategy && (
        <Section title="Price strategy">
          <KV label="Strategy">{d.priceStrategy}</KV>
          {d.bandFloor != null && <KV label="Band floor">{num(d.bandFloor)}</KV>}
          {d.bandCeiling != null && <KV label="Band ceiling">{num(d.bandCeiling)}</KV>}
          {d.offsetPct != null && <KV label="Offset">{pct(d.offsetPct)}</KV>}
          {d.offsetCap != null && <KV label="Offset cap">{num(d.offsetCap)}</KV>}
        </Section>
      )}

      {/* Rationale */}
      {d.rationale && (
        <Section title="Rationale">
          <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-text">
            {d.rationale}
          </pre>
        </Section>
      )}

      {/* Risk decision */}
      <Section title="Risk decision">
        {d.riskApprovedBy && <KV label="Approved by">{d.riskApprovedBy}</KV>}
        {d.riskDeclineReason && (
          <div className="col-span-2 mt-1">
            <div className="text-[10px] uppercase tracking-wide text-text-faint mb-1">Decline reason</div>
            <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-kam-red bg-kam-red-soft border border-kam-red/25 rounded-md p-2">
              {d.riskDeclineReason}
            </pre>
          </div>
        )}
        {!d.riskApprovedBy && !d.riskDeclineReason && (
          <div className="text-xs text-text-faint italic col-span-2">No risk-decision record on this trade.</div>
        )}
      </Section>

      {/* Provenance */}
      <Section title="Provenance">
        <KV label="Requested by">{d.requestedBy}</KV>
        <KV label="Session">
          {d.sessionId
            ? <span className="font-mono text-text-dim text-[10px] break-all">{d.sessionId}</span>
            : '—'}
        </KV>
        {d.kisOrderNo && <KV label="KIS order #">{d.kisOrderNo}</KV>}
      </Section>

      {/* Timeline */}
      <Section title="Timeline">
        {[
          ['Created', d.createdAt],
          ['Approved', d.approvedAt],
          ['Submitted', d.submittedAt],
          ['Filled', d.filledAt],
          ['Updated', d.updatedAt],
        ].map(([label, t]) => (
          <KV key={label} label={label as string}>
            {t ? <>
              <span className="font-mono">{timeOnly(t as string)}</span>
              <span className="text-text-faint text-[10px] ml-1">{ago(t as string)}</span>
            </> : '—'}
          </KV>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-faint mb-2">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
        {children}
      </dl>
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-faint">{label}</dt>
      <dd className="font-mono tabular-nums text-text-dim">{children}</dd>
    </>
  );
}
